import { useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Button,
  Typography,
  Tabs,
  Tab,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";

interface IUltimaImportacao {
  id: number;
  dataHora: string;
  situacao: string;
  nomeArquivo: string;
  podeRemover: boolean;
}

interface IHistoricoImportacao {
  id: number;
  dataHora: string;
  nomeArquivo: string;
}

interface ModalImportacaoProps {
  open: boolean;
  onClose: () => void;
  onImportar: (file: File) => Promise<void>;
  // FASE 03 (item 11): CSV removido — importação/modelo somente em Excel/XLSX.
  onBaixarPlanilhaXlsx: () => Promise<void>;
  onRemoverImportacao?: (id: number) => Promise<void>;
  onDownloadHistorico?: (id: number) => Promise<void>;
  ultimaImportacao?: IUltimaImportacao | null;
  historicoImportacoes?: IHistoricoImportacao[];
  isLoading?: boolean;
  situacaoAtual?: number; // 0=EmEdicao, 1=EmHomologacao, 2=Homologada, 3=DisponivelMercado
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`importacao-tabpanel-${index}`}
      aria-labelledby={`importacao-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export const ModalImportacao: React.FC<ModalImportacaoProps> = ({
  open,
  onClose,
  onImportar,
  onBaixarPlanilhaXlsx,
  onRemoverImportacao,
  onDownloadHistorico,
  ultimaImportacao,
  historicoImportacoes = [],
  isLoading = false,
  situacaoAtual = 0,
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [arquivoSelecionado, setArquivoSelecionado] = useState<File | null>(null);
  const [importando, setImportando] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleSelecionarArquivo = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setArquivoSelecionado(file);
    }
    e.target.value = "";
  };

  const handleImportar = async () => {
    if (!arquivoSelecionado) return;
    setImportando(true);
    try {
      await onImportar(arquivoSelecionado);
      setArquivoSelecionado(null);
      onClose();
    } catch (error) {
      // Erro já tratado na função pai, apenas não fechar o modal
      console.error("Erro na importação:", error);
    } finally {
      setImportando(false);
    }
  };

  const handleRemoverImportacao = async () => {
    if (ultimaImportacao && onRemoverImportacao) {
      await onRemoverImportacao(ultimaImportacao.id);
    }
  };

  // Regra do mockup: só pode importar quando TODAS as aplicabilidades estiverem em "Disponível Mercado" (3).
  // `situacaoAtual` vem do backend (estado agregado).
  const podeImportar = situacaoAtual === 3;
  const tooltipImportar = "Disponível apenas quando todas as aplicabilidades estiverem em Disponível Mercado.";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Typography variant="h6" fontWeight={500}>
          Importação de aplicabilidade
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        {/* Abas */}
        <Box sx={{ borderBottom: 0 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.875rem",
                minHeight: 44,
                backgroundColor: "#e0e0e0",
                borderRadius: "4px 4px 0 0",
                marginRight: 0.5,
                "&.Mui-selected": {
                  backgroundColor: "#3C90AD",
                  color: "#fff",
                },
              },
              "& .MuiTabs-indicator": {
                display: "none",
              },
            }}
          >
            <Tab label="IMPORTAR APLICABILIDADE" />
            <Tab label="HISTÓRICO DE IMPORTAÇÃO" />
          </Tabs>
        </Box>

        {/* Aba Importar */}
        <TabPanel value={tabValue} index={0}>
          {/* FASE 03 (item 11): somente modelo XLSX (CSV removido). */}
          <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              onClick={onBaixarPlanilhaXlsx}
              disabled={isLoading}
              sx={{
                bgcolor: "#3C90AD",
                "&:hover": { bgcolor: "#2d7a94" },
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              BAIXAR MODELO (XLSX)
            </Button>
          </Box>

          {/* Campo de seleção de arquivo */}
          <Box
            sx={{
              display: "flex",
              alignItems: "stretch",
              mb: 3,
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              overflow: "hidden",
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            <Tooltip title={!podeImportar ? tooltipImportar : ""} arrow disableHoverListener={podeImportar}>
              <span>
                <Button
                  variant="contained"
                  onClick={handleSelecionarArquivo}
                  disabled={!podeImportar || isLoading}
                  sx={{
                    bgcolor: "#3C90AD",
                    "&:hover": { bgcolor: "#2d7a94" },
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 0,
                    px: 4,
                  }}
                >
                  BUSCAR
                </Button>
              </span>
            </Tooltip>
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                px: 2,
                bgcolor: "#fff",
              }}
            >
              <Typography
                variant="body2"
                color={arquivoSelecionado ? "primary" : "textSecondary"}
                sx={{ fontWeight: arquivoSelecionado ? 500 : 400 }}
              >
                {arquivoSelecionado?.name || "Nenhum arquivo selecionado"}
              </Typography>
            </Box>
          </Box>

          {/* Botão Importar */}
          {arquivoSelecionado && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
              <Tooltip title={!podeImportar ? tooltipImportar : ""} arrow disableHoverListener={podeImportar}>
                <span>
                  <Button
                    variant="contained"
                    onClick={handleImportar}
                    disabled={importando || !podeImportar}
                    sx={{
                      bgcolor: "#3C90AD",
                      "&:hover": { bgcolor: "#2d7a94" },
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    {importando ? <CircularProgress size={24} color="inherit" /> : "IMPORTAR"}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          )}

          {/* Última planilha importada */}
          {/* ✅ TAREFA 4.2: Só exibir seção se pode remover (conforme mockup) */}
          {ultimaImportacao && ultimaImportacao.podeRemover && (
            <Box>
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ mb: 1, fontWeight: 500 }}
              >
                Última planilha importada
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box
                sx={{
                  border: "2px solid #4caf50",
                  borderRadius: 1,
                  p: 2,
                  mb: 2,
                  bgcolor: "#f1f8f4",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    py: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <Typography variant="body2" fontWeight={500}>
                      {ultimaImportacao.dataHora}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {ultimaImportacao.situacao}
                    </Typography>
                  </Box>
                  {onRemoverImportacao && (
                    <IconButton
                      onClick={handleRemoverImportacao}
                      size="small"
                      disabled={isLoading}
                      sx={{
                        bgcolor: "#d32f2f",
                        color: "#fff",
                        width: 32,
                        height: 32,
                        "&:hover": { bgcolor: "#c62828" },
                        "&:disabled": { bgcolor: "#ccc", color: "#666" },
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                {/* P-15.3: descrição enxuta — substitui dois blocos verbosos por uma linha funcional. */}
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="textSecondary">
                    Remover desfaz as alterações desta importação. Disponível até a aplicabilidade ir para "Disponível Mercado".
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </TabPanel>

        {/* Aba Histórico */}
        <TabPanel value={tabValue} index={1}>
          <TableContainer component={Paper} elevation={0}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                  <TableCell sx={{ fontWeight: 600 }}>Data/hora atualização</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Ação</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historicoImportacoes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2} align="center">
                      <Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
                        Nenhum histórico de importação encontrado.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  historicoImportacoes.map((item, index) => (
                    <TableRow
                      key={item.id}
                      sx={{ bgcolor: index % 2 === 0 ? "#fff" : "#f5f5f5" }}
                    >
                      <TableCell>{item.dataHora}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          onClick={() => onDownloadHistorico?.(item.id)}
                          color="primary"
                          size="small"
                          disabled={isLoading}
                        >
                          <DescriptionIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      </DialogContent>
    </Dialog>
  );
};

export default ModalImportacao;

